import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Beneficiary = () => {
    const navigate = useNavigate();
    const { appId, language, total_voters } = useAppId();
    const lang = language || 'en';

    const t = {
        en: {
            pageTitle: "Beneficiary Dashboard",
            totalVoter: "Total Voters",
            beneficiary: "Beneficiaries",
            nonBeneficiary: "Non Beneficiaries",
            coverage: "Coverage Rate",
            overview: "Coverage Overview",
            schemeChart: "Scheme Distribution",
            schemeList: "Scheme Ranking",
            report: "Detailed Reports",
            boothWise: "Booth Wise Report",
            addressWise: "Address Wise Report",
            phonebookWise: "Phonebook Wise Beneficiary",
            groupWise: "Group Wise Beneficiary",
            boothDesc: "Segment data by polling booths",
            addressDesc: "Segment data by locality & address",
            phonebookDesc: "Segment data by phonebook contacts",
            groupDesc: "Segment data by groups",
            base: "Base"
        },
        hi: {
            pageTitle: "लाभार्थी डैशबोर्ड",
            totalVoter: "कुल मतदाता",
            beneficiary: "लाभार्थी",
            nonBeneficiary: "गैर लाभार्थी",
            coverage: "कवरेज दर",
            overview: "कवरेज अवलोकन",
            schemeChart: "योजना वितरण",
            schemeList: "योजना रैंकिंग",
            report: "विस्तृत रिपोर्ट",
            boothWise: "बूथ अनुसार रिपोर्ट",
            addressWise: "पते अनुसार रिपोर्ट",
            phonebookWise: "फोनबुक अनुसार लाभार्थी",
            groupWise: "ग्रुप अनुसार लाभार्थी",
            boothDesc: "बूथ अनुसार डेटा विभाजित करें",
            addressDesc: "क्षेत्र अनुसार डेटा विभाजित करें",
            phonebookDesc: "फोनबुक संपर्कों अनुसार डेटा विभाजित करें",
            groupDesc: "ग्रुप अनुसार डेटा विभाजित करें",
            base: "आधार"
        },
        gu: {
            pageTitle: "લાભાર્થી ડેશબોર્ડ",
            totalVoter: "કુલ મતદારો",
            beneficiary: "લાભાર્થીઓ",
            nonBeneficiary: "બિન લાભાર્થીઓ",
            coverage: "કવરેજ દર",
            overview: "કવરેજ ઓવરવ્યૂ",
            schemeChart: "યોજના વિતરણ",
            schemeList: "યોજના રેન્કિંગ",
            report: "વિગતવાર રિપોર્ટ",
            boothWise: "બૂથ પ્રમાણે રિપોર્ટ",
            addressWise: "સરનામા પ્રમાણે રિપોર્ટ",
            phonebookWise: "ફોનબુક પ્રમાણે લાભાર્થી",
            groupWise: "ગ્રુપ પ્રમાણે લાભાર્થી",
            boothDesc: "બૂથ પ્રમાણે ડેટા વિભાજિત કરો",
            addressDesc: "વિસ્તાર પ્રમાણે ડેટા વિભાજિત કરો",
            phonebookDesc: "ફોનબુક સંપર્કો પ્રમાણે ડેટા વિભાજિત કરો",
            groupDesc: "ગ્રુપ પ્રમાણે ડેટા વિભાજિત કરો",
            base: "આધાર"
        }
    };

    const txt = t[lang] || t.gu;

    const [loading, setLoading] = useState(true);
    const [totalBen, setTotalBen] = useState(0);
    const [schemes, setSchemes] = useState([]);

    const totalVoterCount = total_voters;

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }
        fetchBeneficiaryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const fetchBeneficiaryData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_scheme_beneficiary_dash`, {
                app_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const result = response.data.data.result;

                if (result.Table && result.Table.length > 0) {
                    setTotalBen(result.Table[0].total || 0);
                }

                if (result.Table1 && Array.isArray(result.Table1)) {
                    setSchemes(result.Table1);
                }
            }
        } catch (error) {
            console.error("Error fetching beneficiary data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Calculations
    const nonBeneficiary = Math.max(0, totalVoterCount - totalBen);
    const beneficiaryPercentage = totalVoterCount > 0 ? Math.round((totalBen / totalVoterCount) * 100) : 0;
    const nonBeneficiaryPercentage = 100 - beneficiaryPercentage;
    const maxSchemeValue = schemes.length > 0 ? Math.max(...schemes.map(s => s.total)) : 1;

    // Sort schemes for ranking (descending)
    const rankedSchemes = [...schemes].sort((a, b) => b.total - a.total);

    // SVG Circle Math
    const circleRadius = 58;
    const circleCircumference = 2 * Math.PI * circleRadius;
    const strokeDashoffset = circleCircumference - (beneficiaryPercentage / 100) * circleCircumference;

    // Y-Axis labels for Bar Chart
    const yAxisMax = Math.ceil(maxSchemeValue / 10000) * 10000 || 10000;
    const yAxisLabels = [yAxisMax, yAxisMax * 0.75, yAxisMax * 0.5, yAxisMax * 0.25, 0];

    const medalColors = ['#f59e0b', '#94a3b8', '#b45309'];

    return (
        <div className="beneficiary-page">
            <div className="header-wrapper">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto" style={{ maxWidth: '1320px' }}>

                {loading ? (
                    <div className="skeleton-wrapper">
                        <div className="skeleton-kpi-row">
                            <div className="skeleton-card skeleton-kpi"></div>
                            <div className="skeleton-card skeleton-kpi"></div>
                            <div className="skeleton-card skeleton-kpi"></div>
                        </div>
                        <div className="skeleton-grid">
                            <div className="skeleton-card skeleton-chart"></div>
                            <div className="skeleton-card skeleton-list"></div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ================= KPI ROW ================= */}
                        <div className="kpi-row fade-in">
                            <div className="kpi-card">
                                <div className="kpi-header">
                                    <div className="kpi-icon-box icon-navy">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                    </div>
                                    <span className="kpi-badge badge-navy">{txt.base}</span>
                                </div>
                                <div className="kpi-body">
                                    <span className="kpi-value text-navy">{totalVoterCount.toLocaleString('en-IN')}</span>
                                    <span className="kpi-label">{txt.totalVoter}</span>
                                </div>
                                <div className="kpi-progress-track">
                                    <div className="kpi-progress-fill fill-navy" style={{ width: '100%' }}></div>
                                </div>
                            </div>

                            <div className="kpi-card">
                                <div className="kpi-header">
                                    <div className="kpi-icon-box icon-emerald">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    </div>
                                    <span className="kpi-badge badge-emerald">{beneficiaryPercentage}%</span>
                                </div>
                                <div className="kpi-body">
                                    <span className="kpi-value text-green">{totalBen.toLocaleString('en-IN')}</span>
                                    <span className="kpi-label">{txt.beneficiary}</span>
                                </div>
                                <div className="kpi-progress-track">
                                    <div className="kpi-progress-fill fill-emerald" style={{ width: `${beneficiaryPercentage}%` }}></div>
                                </div>
                            </div>

                            <div className="kpi-card">
                                <div className="kpi-header">
                                    <div className="kpi-icon-box icon-amber">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                    </div>
                                    <span className="kpi-badge badge-amber">{nonBeneficiaryPercentage}%</span>
                                </div>
                                <div className="kpi-body">
                                    <span className="kpi-value text-orange">{nonBeneficiary.toLocaleString('en-IN')}</span>
                                    <span className="kpi-label">{txt.nonBeneficiary}</span>
                                </div>
                                <div className="kpi-progress-track">
                                    <div className="kpi-progress-fill fill-amber" style={{ width: `${nonBeneficiaryPercentage}%` }}></div>
                                </div>
                            </div>
                        </div>

                        <div className="two-col-wrapper">

                            {/* ================= LEFT COLUMN ================= */}
                            <div className="col-left">

                                {/* Coverage Overview Card */}
                                <h4 className="section-title">{txt.overview}</h4>
                                <div className="overview-card fade-in" style={{ animationDelay: '0.1s' }}>
                                    <div className="donut-section">
                                        <div className="circle-container">
                                            <svg width="150" height="150" viewBox="0 0 150 150">
                                                <defs>
                                                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                        <stop offset="0%" stopColor="#34d399" />
                                                        <stop offset="100%" stopColor="#059669" />
                                                    </linearGradient>
                                                </defs>
                                                <circle cx="75" cy="75" r={circleRadius} stroke="#f1f5f9" strokeWidth="13" fill="none" />
                                                <circle
                                                    cx="75" cy="75"
                                                    r={circleRadius}
                                                    stroke="url(#progressGradient)"
                                                    strokeWidth="13"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeDasharray={circleCircumference}
                                                    strokeDashoffset={strokeDashoffset}
                                                    style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                                                />
                                            </svg>
                                            <div className="circle-content">
                                                <span className="percent-text">{beneficiaryPercentage}<span className="percent-sign">%</span></span>
                                                <span className="voting-lbl">{txt.coverage}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="legend-section">
                                        <div className="legend-row">
                                            <span className="legend-dot dot-emerald"></span>
                                            <span className="legend-label">{txt.beneficiary}</span>
                                            <span className="legend-value text-green">{beneficiaryPercentage}%</span>
                                        </div>
                                        <div className="legend-divider"></div>
                                        <div className="legend-row">
                                            <span className="legend-dot dot-amber"></span>
                                            <span className="legend-label">{txt.nonBeneficiary}</span>
                                            <span className="legend-value text-orange">{nonBeneficiaryPercentage}%</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Bar Chart Section */}
                                <h4 className="section-title mt-4">{txt.schemeChart}</h4>
                                <div className="chart-card fade-in" style={{ animationDelay: '0.15s' }}>
                                    <div className="bar-chart-container">

                                        <div className="y-axis-bg">
                                            {yAxisLabels.map((val, i) => (
                                                <div className="grid-line-wrap" key={i}>
                                                    <span className="y-label">{val === 0 ? '0' : `${val / 1000}k`}</span>
                                                    <div className="grid-line"></div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="bars-area">
                                            {schemes.map((scheme) => {
                                                const heightPct = (scheme.total / yAxisMax) * 100;
                                                return (
                                                    <div className="bar-col" key={scheme.scheme_id}>
                                                        <div className="bar-track">
                                                            <div
                                                                className="bar-fill"
                                                                style={{ height: `${heightPct}%` }}
                                                                title={`${scheme.scheme_name}: ${scheme.total}`}
                                                            >
                                                                <span className="bar-value">{scheme.total.toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                        <div className="bar-label">{scheme.scheme_short_name}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                            </div>

                            {/* ================= RIGHT COLUMN ================= */}
                            <div className="col-right">

                                <h4 className="section-title">{txt.schemeList}</h4>

                                <div className="scheme-list-container fade-in" style={{ animationDelay: '0.2s' }}>
                                    {rankedSchemes.map((scheme, idx) => {
                                        const widthPct = (scheme.total / maxSchemeValue) * 100;
                                        const isTop3 = idx < 3;
                                        return (
                                            <div className="scheme-list-card" key={scheme.scheme_id} style={{ animationDelay: `${0.04 * idx}s` }}>
                                                <div className="scheme-rank" style={isTop3 ? { background: medalColors[idx], color: '#fff' } : {}}>
                                                    {idx + 1}
                                                </div>
                                                <div className="scheme-content">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <span className="scheme-name">{scheme.scheme_name}</span>
                                                        <span className="scheme-total">{scheme.total.toLocaleString('en-IN')}</span>
                                                    </div>
                                                    <div className="scheme-progress-track">
                                                        <div
                                                            className="scheme-progress-fill"
                                                            style={{ width: `${widthPct}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Report Menu */}
                                <h4 className="section-title mt-4">{txt.report}</h4>
                                <div className="report-menu-container fade-in" style={{ animationDelay: '0.25s' }}>

                                    <div className="report-menu-item" onClick={() => navigate('/dis_booth_wise_beneficiary_dash')}>
                                        <div className="report-icon-box icon-amber-tile">
                                            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                        </div>
                                        <div className="report-text-group">
                                            <span className="report-title">{txt.boothWise}</span>
                                            <span className="report-desc">{txt.boothDesc}</span>
                                        </div>
                                        <div className="report-arrow-box">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>

                                    <div className="report-menu-item" onClick={() => navigate('/dis_address_wise_beneficiary_dash')}>
                                        <div className="report-icon-box icon-blue-tile">
                                            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        </div>
                                        <div className="report-text-group">
                                            <span className="report-title">{txt.addressWise}</span>
                                            <span className="report-desc">{txt.addressDesc}</span>
                                        </div>
                                        <div className="report-arrow-box">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>

                                    <div className="report-menu-item" onClick={() => navigate('/dis_phonebook_wise_beneficiary_dash')}>
                                        <div className="report-icon-box icon-emerald-tile">
                                            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                        </div>
                                        <div className="report-text-group">
                                            <span className="report-title">{txt.phonebookWise}</span>
                                            <span className="report-desc">{txt.phonebookDesc}</span>
                                        </div>
                                        <div className="report-arrow-box">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>

                                    <div className="report-menu-item" onClick={() => navigate('/dis_my_group_wise_beneficiary_dash')}>
                                        <div className="report-icon-box icon-purple-tile">
                                            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                        </div>
                                        <div className="report-text-group">
                                            <span className="report-title">{txt.groupWise}</span>
                                            <span className="report-desc">{txt.groupDesc}</span>
                                        </div>
                                        <div className="report-arrow-box">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </div>
                    </>
                )}
            </div>

            <style jsx>{`
                * { box-sizing: border-box; }

                .beneficiary-page {
                    width: 100%;
                    min-height: 100vh;
                    background: linear-gradient(180deg, #f8fafc 0%, #eef1f7 100%);
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    padding-bottom: 40px;
                }

                .header-wrapper {
                    background: white;
                    padding: 0 24px;
                    position: sticky;
                    top: 0;
                    z-index: 50;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
                }

                /* Section Titles */
                .section-title {
                    font-size: 14.5px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 14px;
                    margin-left: 2px;
                    letter-spacing: 0.2px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    text-transform: uppercase;
                }
                .section-title::before {
                    content: '';
                    width: 4px;
                    height: 14px;
                    background: linear-gradient(180deg, #6366f1, #4338ca);
                    border-radius: 4px;
                }

                /* ============ KPI ROW ============ */
                .kpi-row {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 18px;
                    margin-bottom: 26px;
                }
                @media (max-width: 768px) {
                    .kpi-row { grid-template-columns: 1fr; gap: 14px; }
                }

                .kpi-card {
                    background: white;
                    border-radius: 18px;
                    border: 1px solid #eef1f6;
                    padding: 20px 22px;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 8px 20px rgba(15,23,42,0.04);
                    transition: all 0.25s ease;
                    position: relative;
                    overflow: hidden;
                }
                .kpi-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 4px 6px rgba(15,23,42,0.04), 0 16px 30px rgba(15,23,42,0.08);
                }

                .kpi-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
                .kpi-icon-box {
                    width: 42px; height: 42px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                }
                .icon-navy { background: linear-gradient(135deg, #e0e7ff, #c7d2fe); color: #3730a3; }
                .icon-emerald { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #047857; }
                .icon-amber { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #b45309; }

                .kpi-badge {
                    font-size: 11px; font-weight: 700; padding: 4px 10px;
                    border-radius: 20px; letter-spacing: 0.3px;
                }
                .badge-navy { background: #eef2ff; color: #4338ca; }
                .badge-emerald { background: #ecfdf5; color: #059669; }
                .badge-amber { background: #fffbeb; color: #d97706; }

                .kpi-body { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
                .kpi-value { font-size: 26px; font-weight: 800; letter-spacing: -0.8px; line-height: 1.1; }
                .kpi-label { font-size: 12.5px; font-weight: 600; color: #94a3b8; }

                .kpi-progress-track { width: 100%; height: 5px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
                .kpi-progress-fill { height: 100%; border-radius: 3px; transition: width 1.2s cubic-bezier(0.4,0,0.2,1); }
                .fill-navy { background: linear-gradient(90deg, #6366f1, #4338ca); }
                .fill-emerald { background: linear-gradient(90deg, #34d399, #059669); }
                .fill-amber { background: linear-gradient(90deg, #fbbf24, #d97706); }

                .text-navy { color: #1e3a8a; }
                .text-green { color: #059669; }
                .text-orange { color: #d97706; }

                /* Layout */
                .two-col-wrapper {
                    display: grid;
                    grid-template-columns: 1.05fr 0.95fr;
                    gap: 24px;
                    align-items: flex-start;
                }
                @media (max-width: 992px) {
                    .two-col-wrapper { grid-template-columns: 1fr; }
                }

                /* ============ Overview Card (Donut + Legend) ============ */
                .overview-card {
                    background: white;
                    border-radius: 20px;
                    border: 1px solid #eef1f6;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(15,23,42,0.04);
                    overflow: hidden;
                    padding: 26px;
                    gap: 28px;
                    transition: box-shadow 0.3s ease;
                }
                .overview-card:hover { box-shadow: 0 4px 6px rgba(15,23,42,0.04), 0 16px 32px rgba(15,23,42,0.07); }

                .donut-section { display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                .circle-container { position: relative; }
                .circle-content {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                }
                .percent-text { font-size: 30px; font-weight: 800; color: #0f172a; line-height: 1; letter-spacing: -1px; }
                .percent-sign { font-size: 16px; color: #059669; font-weight: 700; }
                .voting-lbl {
                    font-size: 10.5px; font-weight: 700; color: #94a3b8; margin-top: 6px;
                    text-transform: uppercase; letter-spacing: 0.5px;
                }

                .legend-section { flex: 1; display: flex; flex-direction: column; gap: 0; }
                .legend-row { display: flex; align-items: center; gap: 10px; padding: 12px 0; }
                .legend-dot { width: 11px; height: 11px; border-radius: 50%; flex-shrink: 0; }
                .dot-emerald { background: linear-gradient(135deg, #34d399, #059669); }
                .dot-amber { background: linear-gradient(135deg, #fbbf24, #d97706); }
                .legend-label { font-size: 13.5px; font-weight: 600; color: #475569; flex: 1; }
                .legend-value { font-size: 17px; font-weight: 800; }
                .legend-divider { height: 1px; background: #f1f5f9; }

                /* ============ Bar Chart Card ============ */
                .chart-card {
                    background: white;
                    border-radius: 20px;
                    border: 1px solid #eef1f6;
                    padding: 28px 20px 16px 16px;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(15,23,42,0.04);
                    height: 340px;
                    transition: box-shadow 0.3s ease;
                }
                .chart-card:hover { box-shadow: 0 4px 6px rgba(15,23,42,0.04), 0 16px 32px rgba(15,23,42,0.07); }

                .bar-chart-container { position: relative; width: 100%; height: 100%; display: flex; flex-direction: column; }

                .y-axis-bg {
                    position: absolute; top: 0; left: 0; right: 0; bottom: 34px;
                    display: flex; flex-direction: column; justify-content: space-between; z-index: 1;
                }
                .grid-line-wrap { display: flex; align-items: center; gap: 12px; width: 100%; }
                .y-label { font-size: 11px; color: #b0b8c4; width: 28px; text-align: right; font-weight: 500; }
                .grid-line { flex: 1; height: 1px; background: #f4f6f9; }

                .bars-area {
                    position: absolute; top: 0; left: 44px; right: 10px; bottom: 0;
                    display: flex; justify-content: space-around; align-items: flex-end;
                    z-index: 2; padding-bottom: 34px;
                }
                .bar-col { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; width: 100%; position: relative; }
                .bar-track { width: 36px; height: 100%; display: flex; align-items: flex-end; }
                .bar-fill {
                    width: 100%;
                    background: linear-gradient(180deg, #6366f1 0%, #3730a3 100%);
                    border-radius: 8px 8px 0 0;
                    transition: height 1.2s cubic-bezier(0.4,0,0.2,1);
                    position: relative;
                    box-shadow: 0 4px 12px rgba(55,48,163,0.18);
                }
                .bar-fill:hover { filter: brightness(1.1); }
                .bar-value {
                    position: absolute; top: -20px; left: 50%; transform: translateX(-50%);
                    font-size: 10px; font-weight: 700; color: #3730a3;
                    white-space: nowrap; opacity: 0; transition: opacity 0.2s;
                }
                .bar-fill:hover .bar-value { opacity: 1; }
                .bar-label {
                    position: absolute; bottom: -26px; font-size: 11px; font-weight: 600; color: #64748b;
                    text-align: center; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }

                /* ============ Scheme Ranking List ============ */
                .scheme-list-container { display: flex; flex-direction: column; gap: 10px; }
                .scheme-list-card {
                    background: white;
                    border-radius: 14px;
                    border: 1px solid #eef1f6;
                    padding: 14px 16px;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03);
                    transition: all 0.25s ease;
                    animation: slideUp 0.5s ease forwards;
                    opacity: 0;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .scheme-list-card:hover {
                    box-shadow: 0 6px 18px rgba(15,23,42,0.08);
                    transform: translateY(-2px);
                    border-color: #e0e7ff;
                }
                .scheme-rank {
                    width: 30px; height: 30px; border-radius: 10px;
                    background: #f1f5f9; color: #64748b;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 13px; font-weight: 800; flex-shrink: 0;
                }
                .scheme-content { flex: 1; min-width: 0; }
                .scheme-name { font-size: 13px; font-weight: 700; color: #1e293b; max-width: 72%; line-height: 1.35; }
                .scheme-total { font-size: 14px; font-weight: 800; color: #059669; white-space: nowrap; }
                .scheme-progress-track { width: 100%; height: 6px; background: #f1f5f9; border-radius: 4px; margin-top: 8px; overflow: hidden; }
                .scheme-progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #10b981, #059669);
                    border-radius: 4px;
                    transition: width 1.2s cubic-bezier(0.4,0,0.2,1);
                }

                /* ============ Report Tiles ============ */
                .report-menu-container { display: flex; flex-direction: column; gap: 12px; }
                .report-menu-item {
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #eef1f6;
                    padding: 18px 18px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 6px 16px rgba(15,23,42,0.03);
                }
                .report-menu-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(15,23,42,0.08);
                    border-color: #e0e7ff;
                }
                .report-icon-box {
                    width: 48px; height: 48px; border-radius: 14px;
                    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
                }
                .icon-amber-tile { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #b45309; }
                .icon-blue-tile { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #1d4ed8; }
                .icon-emerald-tile { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #047857; }
                .icon-purple-tile { background: linear-gradient(135deg, #ede9fe, #ddd6fe); color: #6d28d9; }

                .report-text-group { flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
                .report-title { font-size: 14.5px; font-weight: 700; color: #0f172a; }
                .report-desc { font-size: 12px; color: #94a3b8; font-weight: 500; }

                .report-arrow-box {
                    width: 32px; height: 32px; border-radius: 10px;
                    background: #f8fafc; color: #94a3b8;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.25s ease;
                }
                .report-menu-item:hover .report-arrow-box { background: #4338ca; color: white; transform: translateX(2px); }

                /* Animations */
                .fade-in { animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards; opacity: 0; }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Skeleton Loading */
                .skeleton-wrapper { display: flex; flex-direction: column; gap: 24px; }
                .skeleton-kpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
                @media (max-width: 768px) { .skeleton-kpi-row { grid-template-columns: 1fr; } }
                .skeleton-grid { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 24px; }
                @media (max-width: 992px) { .skeleton-grid { grid-template-columns: 1fr; } }
                .skeleton-card {
                    border-radius: 18px;
                    background: linear-gradient(90deg, #eef1f6 25%, #f8fafc 50%, #eef1f6 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.5s infinite;
                }
                .skeleton-kpi { height: 130px; }
                .skeleton-chart { height: 340px; }
                .skeleton-list { height: 340px; }
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }

                /* Mobile Optimization */
                @media (max-width: 480px) {
                    .overview-card { flex-direction: column; text-align: center; }
                    .legend-row { justify-content: center; }
                    .bar-track { width: 24px; }
                    .y-label { width: 20px; font-size: 10px; }
                    .bars-area { left: 30px; }
                    .kpi-value { font-size: 22px; }
                    .scheme-name { max-width: 60%; }
                }
            `}</style>
        </div>
    );
};

export default Beneficiary;